import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Plus, X } from "lucide-react";
import { EmptyState } from "./ClientePortal";

const DIRECTION_LABEL = { retiro: "Retiro (se lo llevan)", ingreso: "Ingreso (lo traen)" };
const STATUS_LABEL = { pendiente: "Pendiente", cumplida: "Cumplida", cancelada: "Cancelada" };
const STATUS_COLOR = { pendiente: "#d97706", cumplida: "#16a34a", cancelada: "#6B655A" };

const EMPTY_FORM = { direction: "retiro", description: "", carrier_name: "", scheduled_for: "", notes: "" };

/** Autorización de retiro/ingreso de elementos del domicilio de un
 * inquilino (ej. "se llevan mi televisor a reparar"), para que el
 * vigilador la vea y lo deje pasar. */
export function DwellingAuthsTab({ clientId }) {
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setRows(null);
    try { const { data } = await api.get("/portal/dwelling-auths", { params: { client_id: clientId } }); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); setRows([]); }
  };
  useEffect(() => { if (clientId) load(); }, [clientId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.carrier_name.trim()) return toast.error("Completá el elemento y quién lo retira/ingresa");
    setBusy(true);
    try {
      await api.post("/portal/dwelling-auths", {
        client_id: clientId, direction: form.direction, description: form.description,
        carrier_name: form.carrier_name, scheduled_for: form.scheduled_for || null, notes: form.notes || null,
      });
      toast.success("Autorización registrada");
      setForm(EMPTY_FORM); setCreating(false);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("¿Cancelar esta autorización?")) return;
    try { await api.delete(`/portal/dwelling-auths/${id}`); toast.success("Autorización cancelada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="tenant-dwelling-auths-tab">
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button onClick={() => setCreating((v) => !v)} className="btn-primary" style={{ padding: "8px 14px", fontSize: 12 }} data-testid="dwelling-auth-add-btn">
          {creating ? <><X size={13} style={{ marginRight: 6 }}/>Cancelar</> : <><Plus size={13} style={{ marginRight: 6 }}/>Nueva autorización</>}
        </button>
      </div>
      {creating && (
        <form onSubmit={submit} className="card" style={{ marginBottom: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} data-testid="dwelling-auth-form">
          <div className="field" style={{ margin: 0 }}>
            <label>Tipo *</label>
            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} data-testid="dwelling-auth-direction">
              <option value="retiro">Retiro (se lo llevan)</option>
              <option value="ingreso">Ingreso (lo traen)</option>
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}><label>¿Quién lo retira/ingresa? *</label><input value={form.carrier_name} onChange={(e) => setForm({ ...form, carrier_name: e.target.value })} placeholder="ej. Juan Pérez, service técnico" data-testid="dwelling-auth-carrier" /></div>
          <div className="field" style={{ margin: 0, gridColumn: "1 / -1" }}><label>Elemento *</label><input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="ej. Televisor LED 43 pulgadas" data-testid="dwelling-auth-description" /></div>
          <div className="field" style={{ margin: 0 }}><label>Fecha/hora prevista</label><input type="datetime-local" value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} data-testid="dwelling-auth-scheduled" /></div>
          <div className="field" style={{ margin: 0 }}><label>Notas</label><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="dwelling-auth-notes" /></div>
          <button type="submit" disabled={busy} className="btn-primary" style={{ gridColumn: "1 / -1" }} data-testid="dwelling-auth-submit">{busy ? "Guardando..." : "Registrar autorización"}</button>
        </form>
      )}
      {rows === null ? <div className="skel" style={{ height: 80 }} /> : rows.length === 0 ? <EmptyState>Sin autorizaciones registradas.</EmptyState> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="dwelling-auths-list">
          {rows.map((r) => (
            <div key={r.id} className="card" data-testid={`dwelling-auth-${r.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6 }}>
                <div style={{ fontWeight: 800, fontSize: 13 }}>{DIRECTION_LABEL[r.direction]} · {r.description}</div>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: STATUS_COLOR[r.status] }}>{STATUS_LABEL[r.status]}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--invict-text-muted)", margin: "4px 0" }}>
                Retira/ingresa: {r.carrier_name}{r.scheduled_for ? ` · ${new Date(r.scheduled_for).toLocaleString("es-AR")}` : ""}
              </div>
              {r.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{r.notes}</div>}
              {r.status === "pendiente" && (
                <button onClick={() => cancel(r.id)} className="btn-ghost" style={{ padding: "6px 12px", fontSize: 11, marginTop: 6 }} data-testid={`dwelling-auth-cancel-${r.id}`}>Cancelar</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
